import { usePostHog } from 'posthog-js/react';
import { useEffect, useState, useMemo } from 'react';

export interface RetellAgent {
  agentId: string;
  phone: string;
}

export interface RetellBrandConfig {
  name: string;
  defaultLanguage: string;
  agents: Record<string, RetellAgent>;
}

export interface RetellConfig {
  countries: string[];
  brands: Record<string, RetellBrandConfig>;
}

export interface RetellAgentConfig {
  countries: string[];
  phoneNumber: string;
  agentId: string;
}

const RETELL_CONFIG_FEATURE_FLAG = 'retell-callback-config';

// Cache for the config to avoid reloading on every hook call
let configCache: RetellConfig | null = null;
let configLoadingPromise: Promise<RetellConfig | null> | null = null;

/**
 * Loads Retell configuration from PostHog feature flag.
 * Returns null if config cannot be loaded or parsed.
 * Uses caching to avoid multiple simultaneous loads.
 */
export const loadRetellConfig = async (
  posthog: ReturnType<typeof usePostHog> | null,
): Promise<RetellConfig | null> => {
  // Return cached config if available
  if (configCache) {
    return configCache;
  }

  // If already loading, return the existing promise
  if (configLoadingPromise) {
    return configLoadingPromise;
  }

  // Start loading
  configLoadingPromise = (async () => {
    if (!posthog) {
      console.warn('PostHog is not available, cannot load Retell config');
      return null;
    }

    try {
      const featureFlagPayload = posthog.getFeatureFlagPayload(RETELL_CONFIG_FEATURE_FLAG);
      if (featureFlagPayload) {
        const config = parseRetellConfig(featureFlagPayload);
        if (config) {
          configCache = config;
          return config;
        }
      } else {
        console.warn('Retell config feature flag payload is not available');
      }
    } catch (e) {
      console.warn('Failed to load Retell config from feature flag:', e);
    }

    return null;
  })();

  const result = await configLoadingPromise;
  configLoadingPromise = null;
  return result;
};

/**
 * Parses JSON data into a RetellConfig model, extracting countries,
 * brands, and their associated agent configurations
 */
const parseRetellConfig = (jsonData: unknown): RetellConfig | null => {
  try {
    const config = jsonData as RetellConfig;
    if (!config) return null;
    const brandsMap = config.brands;
    const brands: Record<string, RetellBrandConfig> = {};

    if (brandsMap) {
      for (const [brandPhoneNumber, brandData] of Object.entries(brandsMap)) {
        const agentsMap = brandData.agents;
        const agents: Record<string, RetellAgent> = {};

        if (agentsMap) {
          for (const [language, agentData] of Object.entries(agentsMap)) {
            agents[language] = {
              agentId: agentData.agentId,
              phone: agentData.phone,
            };
          }
        }

        brands[brandPhoneNumber] = {
          name: brandData.name,
          defaultLanguage: brandData.defaultLanguage,
          agents,
        };
      }
    }

    return {
      countries: config.countries || [],
      brands,
    };
  } catch (e) {
    console.error('Failed to parse Retell config:', e);
    return null;
  }
};

/**
 * Retrieves the agent configuration for a specific phone number and language.
 * Looks up the brand config by phone number.
 * Falls back to brand's default language if language is not provided or not supported.
 * Returns null if phone number is not found in config or no valid configuration can be determined.
 */
export const getRetellAgentConfig = (
  config: RetellConfig | null,
  phoneNumber: string | null | undefined,
  language: string | null | undefined,
): RetellAgentConfig | null => {
  if (!config) return null;

  // Return null if phone number is not provided
  if (!phoneNumber || phoneNumber.trim() === '') return null;

  // Lookup brand config by phone number
  const brandConfig = config.brands[phoneNumber];
  if (!brandConfig) return null;

  // Determine which language to use
  const targetLanguage =
    language && brandConfig.agents[language] ? language : brandConfig.defaultLanguage;

  // Get agent for the target language
  const agent = brandConfig.agents[targetLanguage];
  if (!agent) return null;

  return {
    countries: config.countries,
    phoneNumber: agent.phone,
    agentId: agent.agentId,
  };
};

/**
 * Hook to get Retell agent config based on phone number and language
 */
export const useRetellAgentConfig = (
  phoneNumber: string | null | undefined,
  language: string | null | undefined,
): RetellAgentConfig | null => {
  const posthog = usePostHog();
  const [config, setConfig] = useState<RetellConfig | null>(null);

  useEffect(() => {
    loadRetellConfig(posthog).then(setConfig);
  }, [posthog]);

  return useMemo(() => {
    return getRetellAgentConfig(config, phoneNumber, language);
  }, [config, phoneNumber, language]);
};
